import cities from './territory-cities.json';
import type {PortfolioClientDirectoryRow,PortfolioPropertyDirectoryRow} from './types';
import {extraCities} from './territory-extra-cities';

export type CityReference = {latitude:number; longitude:number; place:string; state:string; source:string; precision:string};
export type TerritoryClient = PortfolioClientDirectoryRow & {location:CityReference|null; niche:string};
export type TerritoryProperty = PortfolioPropertyDirectoryRow & {location:CityReference|null; niche:string};
const references:Record<string,CityReference>={...cities,...extraCities};
const normalize=(value:string)=>value.trim().toLowerCase().replace(/\./g,'').replace(/\s+/g,' ');
const lookup=new Map(Object.entries(references).flatMap(([key,value])=>[
 [normalize(key),value], [normalize(`${value.place}, ${value.state}`),value],
 [normalize(`${value.place}, ${key.split(',').pop()?.trim()}`),value],
]));
export const cityLocation=(area:string|null):CityReference|null=>area?lookup.get(normalize(area))||null:null;
export const locationKey=(location:CityReference|null)=>location?`${location.latitude},${location.longitude}`:null;
export function nicheFor(account:string|null):string {
 if (!account) return 'Unclassified';
 if (/drain/i.test(account)) return 'Drainage';
 if (/wall/i.test(account)) return 'Retaining walls';
 if (/deck/i.test(account)) return 'Decks';
 if (/fenc/i.test(account)) return 'Fencing';
 if (/crawl/i.test(account)) return 'Crawlspace';
 if (/paver|paving/i.test(account)) return 'Pavers';
 if (/concrete/i.test(account)) return 'Concrete';
 if (/tree/i.test(account)) return 'Tree service';
 if (/insulat/i.test(account)) return 'Insulation';
 if (/pool/i.test(account)) return 'Pool service';
 if (/landscap|lawn/i.test(account)) return 'Landscaping';
 if (/sprinkler|irrigation/i.test(account)) return 'Irrigation';
 return 'Unclassified';
}
export function mapClients(clients:PortfolioClientDirectoryRow[]):TerritoryClient[] {
 return clients.map(client=>({...client,niche:nicheFor(client.account),location:
  cityLocation(client.area),
 })).sort((a,b)=>a.client_name.localeCompare(b.client_name)||a.client_id-b.client_id);
}
export function mapProperties(properties:PortfolioPropertyDirectoryRow[]):TerritoryProperty[]{
 return properties.map(p=>({...p,location:p.city&&p.state?cityLocation(`${p.city}, ${p.state}`):null,
  niche:nicheFor(`${p.property_name} ${p.domain||''}`),
 })).sort((a,b)=>(a.domain||a.property_name).localeCompare(b.domain||b.property_name)||a.property_id-b.property_id);
}
export function filterProperties(properties:TerritoryProperty[],search:string,niche:string,coverage:string,clientScope:string){
 const query=search.trim().toLowerCase();
 return properties.filter(p=>(!query||[p.domain,p.property_name,p.city,p.state,p.client_name,p.company_name,p.account].some(v=>v?.toLowerCase().includes(query)))
  &&(!niche||p.niche===niche)&&(!coverage||(coverage==='located'?Boolean(p.location):!p.location))
  &&(!clientScope||(clientScope==='unassigned'?p.client_id===null:p.client_id===Number(clientScope))));
}
export function groupMarkets(clients:TerritoryClient[],properties:TerritoryProperty[]){
 const groups=new Map<string,{key:string;location:CityReference;clients:TerritoryClient[];properties:TerritoryProperty[]}>();
 for(const client of clients){if(!client.location)continue;const key=locationKey(client.location)!;
  const group=groups.get(key)||{key,location:client.location,clients:[],properties:[]};group.clients.push(client);groups.set(key,group);}
 for(const property of properties){if(!property.location)continue;const key=locationKey(property.location)!;
  const group=groups.get(key)||{key,location:property.location,clients:[],properties:[]};group.properties.push(property);groups.set(key,group);}
 return [...groups.values()].sort((a,b)=>a.location.place.localeCompare(b.location.place));
}
export function websiteHref(domain:string|null):string|null{
 if(!domain)return null;
 try{const url=new URL(/^https?:\/\//i.test(domain)?domain:`https://${domain}`);
  if(!['https:','http:'].includes(url.protocol)||url.username||url.password||!url.hostname.includes('.')||/\s/.test(domain))return null;
  return url.href;
 }catch{return null;}
}
export function groupLocations(clients:TerritoryClient[]) {
 const groups=new Map<string,{key:string; location:CityReference; clients:TerritoryClient[]}>();
 for(const client of clients){
  if(!client.location) continue;
  const key=`${client.location.latitude},${client.location.longitude}`;
  const group=groups.get(key)||{key,location:client.location,clients:[]};
  group.clients.push(client);groups.set(key,group);
 }
 return [...groups.values()];
}
export function filterClients(clients:TerritoryClient[], search:string, niche:string, status:string, coverage:string){
 const query=search.trim().toLowerCase();
 return clients.filter(c=>(!query||[c.client_name,c.company_name,c.area,c.account,c.contact_name].some(v=>v?.toLowerCase().includes(query)))
  &&(!niche||c.niche===niche)&&(!status||c.status===status)
  &&(!coverage||(coverage==='located'?Boolean(c.location):!c.location)));
}
